import { initializeApp } from 'firebase/app';
import { initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Multi-tenant: each Firebase Hosting deploy serves its own config at
// /__/firebase/init.json. We load it at boot so a single build can run
// across nccd-planner (dev), nccd-stbrigids, and any future school project
// without rebuilding per-tenant. The fallback below is only used for
// local dev when /__/firebase/init.json isn't reachable.
//
// Note: Firebase Web API keys are public by design (they identify the
// project, not authenticate users — security comes from Firebase Auth +
// Firestore Security Rules). The values are sourced from .env.local at
// build time so the literal key never appears in this file. See
// .env.example for the variable names.
const FALLBACK_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Local/emulator mode (localhost or 127.0.0.1) always talks to the Firebase
// emulators, which ignore the API key — but the Auth SDK still refuses to
// initialise with an empty apiKey (throws auth/invalid-api-key). In CI there's
// no .env.local, so the VITE_* vars are undefined; fall back to demo values so
// the app boots. projectId MUST default to nccd-planner so the app reads the
// same emulator namespace the e2e seed writes to.
const EMULATOR_CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'nccd-planner.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'nccd-planner',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'nccd-planner.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '000000000000',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || 'demo-app-id',
};

async function loadFirebaseConfig() {
  if (typeof location === 'undefined' || location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
    return EMULATOR_CONFIG;
  }
  try {
    const res = await fetch('/__/firebase/init.json', { cache: 'no-store' });
    if (!res.ok) throw new Error(`init.json HTTP ${res.status}`);
    const cfg = await res.json();
    // Use the firebaseapp.com authDomain that init.json returns. That's the
    // URI Firebase auto-whitelists on the auto-created Google OAuth client
    // when Google sign-in is enabled, so redirect_uri_mismatch can't happen
    // out of the box. We use signInWithPopup exclusively (auth.js), which
    // uses postMessage to bridge the popup-to-parent communication —
    // postMessage works across origins, so cross-origin sessionStorage
    // partitioning (the thing that breaks signInWithRedirect on iOS Safari
    // and in-app browsers) is not a concern here.
    //
    // ...that was the reasoning, and it is no longer the whole story. The
    // cross-origin authDomain costs us more than it saves:
    //
    //   - signInWithPopup still loads a hidden iframe + handler on the auth
    //     domain, so browsers that block or partition THIRD-PARTY storage
    //     (Chrome incognito by default, Safari ITP, extensions, the ongoing
    //     third-party-cookie phase-out) can stall sign-in. Confirmed at
    //     St Brigid's: a teacher who could not sign in in a fresh incognito
    //     window.
    //   - Google serves accounts.google.com with COOP same-origin, so the SDK
    //     can neither poll popup.closed nor close the popup — the console
    //     fills with "Cross-Origin-Opener-Policy policy would block the
    //     window.close call" and a dismissed popup isn't detected.
    //
    // Serving the handler from the app's OWN origin fixes both outright.
    //
    // ONLY hosts listed here get this treatment, because it requires manual
    // console config per project that we cannot verify at runtime. To add a
    // school: in Google Cloud Console -> APIs & Services -> Credentials -> the
    // Firebase "Web client (auto created by Google Service)", add
    //   https://<host>                     to Authorised JavaScript origins
    //   https://<host>/__/auth/handler      to Authorised redirect URIs
    // (and the same handler URL as a Reply URL on the Azure app registration
    // if that school uses Microsoft sign-in), confirm <host> is in Firebase
    // Auth -> Settings -> Authorised domains, THEN add it below. Get that
    // order wrong and Google returns redirect_uri_mismatch — i.e. nobody can
    // sign in — so never add a host here speculatively.
    // EMPTY ON PURPOSE. Both hosts were enabled and immediately produced
    // Error 400: redirect_uri_mismatch, because the OAuth client had not (yet)
    // accepted the handler URI — Google's own console warns the change takes
    // "five minutes to a few hours" to take effect. A host goes back in this
    // list ONLY after it has been proven with the localStorage override on a
    // real sign-in:
    //   localStorage.setItem('nccd:authDomain', '<host>'); location.reload();
    // That override path is per-browser, so it can never take a school down.
    const SAME_ORIGIN_AUTH_HOSTS = new Set([
      'practice4stbrigids.easyclarity4growth.com', // nccd-practice4stbrigids
      'stbrigids.easyclarity4growth.com',          // nccd-stbrigids (live)
    ]);
    if (SAME_ORIGIN_AUTH_HOSTS.has(location.hostname)) cfg.authDomain = location.hostname;

    // Manual per-browser override always wins — it's how we test a new host
    // (or back out of one) without a deploy.
    try {
      const override = localStorage.getItem('nccd:authDomain');
      if (override) cfg.authDomain = override;
    } catch {}
    return cfg;
  } catch (e) {
    console.warn('Falling back to bundled Firebase config:', e?.message);
    return FALLBACK_CONFIG;
  }
}

const firebaseConfig = await loadFirebaseConfig();
const app = initializeApp(firebaseConfig);

// The real project id for this tenant (init.json is per-deploy). Used to gate
// destructive demo/seed tools: they show on internal dev (nccd-planner), any
// per-school "-dev" twin, and per-school PRACTICE/sandbox projects
// (nccd-practice4<school>) — and are HIDDEN on live school production sites.
export const PROJECT_ID = firebaseConfig.projectId || '';
export const IS_DEV_ENV = PROJECT_ID === 'nccd-planner' || /-dev$/.test(PROJECT_ID) || /practice/i.test(PROJECT_ID);

// === App Check (per-tenant reCAPTCHA Enterprise) ===
// Site key per project. ONLY projects listed here initialise App Check; every
// other tenant skips it with zero behaviour change. Initialising merely makes
// the client START sending attestation tokens — it blocks NOTHING. Enforcement
// (rejecting tokenless requests) is a separate, later console step per project,
// switched on only after the App Check "Request metrics" show verified tokens.
// To onboard a school: register its web app + reCAPTCHA key (see runbook), then
// add its projectId → siteKey here.
// CSP (firebase.json) now allows the reCAPTCHA Enterprise script/frame/connect
// domains (https://www.google.com/recaptcha/ + connect https://www.google.com).
const APPCHECK_SITE_KEYS = {
  // RESTORED 14 Aug 2026 after verifying attestation end-to-end from the
  // practice domain (real grecaptcha token → exchange → 200 + App Check JWT).
  // It was parked for a day when the exchange 403'd and the SDK's 24h
  // self-throttle turned one failure into a day of console spam + failing AI
  // callables (functions/src/utils/appcheck.ts requires a token on THIS
  // project only). If 403s recur, check: key's allowedDomains covers every
  // serving host; App Check provider config; and remember the throttle makes
  // one bad attempt look like a permanent outage until reload.
  'nccd-practice4stbrigids': '6LcWRWEtAAAAAEWDXgrN5qT6_iqjAHltiPIamyhg',
};
const _appCheckSiteKey = APPCHECK_SITE_KEYS[PROJECT_ID];
if (_appCheckSiteKey && typeof location !== 'undefined'
    && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
  // Dynamic import so app-check only loads for tenants that use it.
  import('firebase/app-check').then(({ initializeAppCheck, ReCaptchaEnterpriseProvider }) => {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(_appCheckSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) { console.warn('App Check init skipped (non-fatal):', e?.message); }
  }).catch(() => {});
}

// Plain Firestore client (no persistent IndexedDB cache). We trialled
// persistentLocalCache + persistentMultipleTabManager for offline/repeat-read
// speed, but the app only ever uses getDoc/getDocs (server-fresh reads), so the
// online payoff was nil while the cost was real: every read got serialised into
// IndexedDB (write amplification on the whole-roster boot load) and the
// multi-tab manager added leader-election latency. That made the live site
// SLOWER, so we reverted. (Live listeners on the open student now exist —
// services/live-student.js.)
//
// TRANSPORT — history matters here:
// Some schools run locked-down managed Chrome / proxies that BLOCK Firestore's
// default WebChannel streaming transport — the symptom is a login that hangs
// forever at the first getDoc (authStage 'reading-user-doc') for those users
// while everyone else is fine. experimentalAutoDetectLongPolling was NOT enough
// on its own (a confirmed St Brigid's teacher still hung with it on) — the
// auto-detect probe itself relies on the stream and doesn't reliably fall back
// on a black-holing proxy. We then ran experimentalForceLongPolling for
// EVERYONE, which fixed that teacher but taxed every other user with the slow
// transport (plain HTTPS request/response instead of the stream) — a real cost
// with a whole staff online at once.
//
// Now: fast streaming by default, with a STICKY PER-BROWSER long-poll fallback.
// If the first Firestore read of a session stalls past both of its time-boxed
// attempts (auth.js readUserDocWithRetry — the exact read that stranded the
// proxied teacher), fallBackToLongPolling() stamps this browser and reloads;
// from then on that browser initialises with forced long-polling. One slow
// first boot (~18s) for a proxied user, full-speed streaming for everyone else.
const TRANSPORT_KEY = 'easy-transport';
const TRANSPORT_RELOAD_AT = 'easy-transport-reload-at';
let _forcedLongPoll = false;
try { _forcedLongPoll = localStorage.getItem(TRANSPORT_KEY) === 'longpoll'; } catch { /* no storage — default to streaming */ }
export const usingForcedLongPolling = _forcedLongPoll;

/**
 * Flip this browser to forced long-polling (sticky) and reload. Called when
 * the streaming transport looks blocked (first read stalled through both
 * time-boxed attempts). Returns true if a reload was initiated. Loop-guarded:
 * no-op when already forced, when storage is unavailable (the flag couldn't
 * stick, so reloading would loop), or within 60s of a previous transport
 * reload.
 */
export function fallBackToLongPolling(reason) {
  if (_forcedLongPoll) return false;
  try {
    const last = Number(localStorage.getItem(TRANSPORT_RELOAD_AT) || 0);
    if (Date.now() - last < 60_000) return false;
    localStorage.setItem(TRANSPORT_KEY, 'longpoll');
    localStorage.setItem(TRANSPORT_RELOAD_AT, String(Date.now()));
  } catch { return false; }
  console.warn('[EASY] Firestore streaming appears blocked (' + reason + ') — switching this browser to long-polling and reloading.');
  try { location.reload(); } catch { return false; }
  return true;
}

export const db = initializeFirestore(app, _forcedLongPoll
  ? { experimentalForceLongPolling: true }
  : { experimentalAutoDetectLongPolling: true });
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'australia-southeast1');

// Connect to emulators in development. Use the explicit IPv4 loopback
// (127.0.0.1), NOT "localhost": the Firebase emulators bind to 127.0.0.1, but
// on Linux CI runners the browser resolves "localhost" to ::1 (IPv6) first, so
// emulator calls would hang/refuse and every e2e sign-in would time out. The
// hostname guard still accepts a page served on either localhost or 127.0.0.1.
if (
  (location.hostname === 'localhost' || location.hostname === '127.0.0.1') &&
  import.meta.env.VITE_USE_EMULATOR !== 'false'
) {
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectAuthEmulator(auth, 'http://127.0.0.1:9099');
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}
